import { ApiRequestStatus, appRoutes, DashboardWalletSummary, HomeCard, walletPageMessages, walletCommonSliceActions } from 'azshared';
import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Card, CardSize } from '../../../../../components/card/card';
import { Loader, LoaderSize } from '../../../../../components/loader/loader';
import { AppSvgIcon } from '../../../../../components/svg-icon/app-svg-icon';
import { HomeMyWalletCardBars } from './home-my-wallet-card-bars';
import { HomeMyWalletCardCarousel } from './home-my-wallet-card-carousel';
import { HomeMyWalletCardShowBalance } from './home-my-wallet-card-show-balance';
import { HomeMyWalletCardTotalAmount } from './home-my-wallet-card-total-amount';
import './home-my-wallet-card.scss';

interface HomeMyWalletCardProps {
  card: HomeCard<DashboardWalletSummary>;
  dossierRequestStatus: ApiRequestStatus;
  setFlagShowBalance: (value: boolean) => void;
  pirBoxDisclaimer: string;
}

export const HomeMyWalletCard = (props: HomeMyWalletCardProps) => {
  const { formatMessage } = useIntl();
  const { card, setFlagShowBalance, dossierRequestStatus, pirBoxDisclaimer } = props;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const goToWalletPage = useCallback(() => {
    const targetTab = pirBoxDisclaimer === 'SHARED_ACCOUNTS' ? 'SHARED_ACCOUNTS' : 'MY_WALLET';

    dispatch(walletCommonSliceActions.setSelectedWalletSection(targetTab));

    navigate(appRoutes.Wallet);
  }, [navigate, dispatch, pirBoxDisclaimer]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        goToWalletPage();
      }
    },
    [goToWalletPage]
  );

  const isLoading = useMemo(() => dossierRequestStatus === 'loading' || card.requestStatus === 'loading', [dossierRequestStatus, card]);
  const isWalletEmpty = card.value == null || card.value.totalAmount == null || card.value.totalAmount.value === 0 || card.value.totalAmount.value === 0;

  const cardLabel = useMemo(() => {
    if (pirBoxDisclaimer === 'SHARED_ACCOUNTS') {
      return formatMessage(walletPageMessages.myWalletLabelShared);
    }
    return formatMessage(walletPageMessages.myWalletLabel);
  }, [formatMessage, pirBoxDisclaimer]);

  return (
    <div className='home-wallet'>
      <Card sizeVariant={CardSize.MAXI}>
        <div className='home-wallet-card-header'>
          <div className='home-wallet-card-header-title'>
            <AppSvgIcon icon='home-wallet' />
            <h4> {cardLabel} </h4>
          </div>
          {!isLoading && !isWalletEmpty && <HomeMyWalletCardShowBalance card={card} onShowBalance={setFlagShowBalance} />}
        </div>
        {!isLoading && !isWalletEmpty && (
          <>
            <HomeMyWalletCardTotalAmount card={card} />
            <HomeMyWalletCardCarousel card={card} />
            <HomeMyWalletCardBars pirBoxDisclaimer={pirBoxDisclaimer} card={card} />
            <div className='link-go-to-wallet' onClick={goToWalletPage} onKeyDown={handleKeyDown} tabIndex={0}>
              <span>{formatMessage(walletPageMessages.walletWidgetLinkGoToWallet)}</span>
              <AppSvgIcon icon='home-arrow' />
            </div>
          </>
        )}
        {!isLoading && isWalletEmpty && (
          <>
            <div className='empty-wallet'>
              <span>{formatMessage(walletPageMessages.emptyWallet)}</span>
            </div>
            <div className='link-go-to-wallet' onClick={goToWalletPage} onKeyDown={handleKeyDown} tabIndex={0}>
              <span>{formatMessage(walletPageMessages.walletWidgetLinkGoToWallet)}</span>
              <AppSvgIcon icon='home-arrow' />
            </div>
          </>
        )}

        {isLoading && (
          <div className='home-wallet-card-loader'>
            <Loader loadersize={LoaderSize.XL} />
          </div>
        )}
      </Card>
    </div>
  );
};
